import { el } from "./dom.js";
import { MAG } from "../utils/magazine.js";
import { showActionSheet } from "./item-menu.js";
import { showToast } from "./toast.js";
import { offerUndoChanges } from "./undo.js";
import {
	ENGAGEMENT_STATUSES,
	OWNERSHIP_STATUSES,
	BOOK_OWNERSHIP_STATUSES,
	statusLabel,
} from "./status-badge.js";
import { updateMediaItem, deleteMediaItem, restoreMediaItem, getMediaRecord } from "../db/media.js";
import { listGroups, addItemToGroup, createManualGroup } from "../db/groups.js";
import { rerenderRoute } from "../main.js";

// Bulk actions on a category list (roadmap #17, and #18: a quick status change
// is a Select of one item). "Select" turns taps on items into selection
// toggles; a bar at the bottom offers Status / Ownership / Group / Delete for
// everything selected, each with an Undo. Long-press stays drag-to-group.
//
//   const bulk = bulkSelect({ mediaType });   // button for the toolbar
//   bulk.attach(listContainer);              // after the list container exists

const SELECTED_STYLE = { outline: `3px solid ${MAG.lime}`, outlineOffset: "2px", borderRadius: "14px" };

export function bulkSelect({ mediaType }) {
	const selected = new Set();
	let active = false;
	let container = null;
	let bar = null;

	const button = el("button.btn.btn--small", {
		type: "button",
		title: "Select several items to change at once",
		onclick: () => (active ? stop() : start()),
	}, "Select");

	const label = el("span", { style: { flex: "1", fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "12px", color: MAG.ink } });

	function paint() {
		label.textContent = selected.size ? `${selected.size} selected` : "Tap items to select";
		for (const node of container?.querySelectorAll('[data-drag-kind="item"]') ?? []) {
			const on = selected.has(node.dataset.dragId);
			Object.assign(node.style, on ? SELECTED_STYLE : { outline: "", outlineOffset: "" });
			node.setAttribute("aria-pressed", on ? "true" : "false");
		}
		for (const b of bar?.querySelectorAll("[data-needs-selection]") ?? []) b.disabled = selected.size === 0;
	}

	// Taps on items select instead of opening while active (capture phase, so
	// the item's own click handler never sees them).
	function onClick(e) {
		if (!active) return;
		const node = e.target.closest?.('[data-drag-kind="item"]');
		if (!node || !container.contains(node)) return;
		e.preventDefault();
		e.stopPropagation();
		const id = node.dataset.dragId;
		if (selected.has(id)) selected.delete(id);
		else selected.add(id);
		paint();
	}

	const barButton = (text, run, { needs = true } = {}) =>
		el("button", {
			type: "button",
			dataset: needs ? { needsSelection: "1" } : {},
			onclick: run,
			style: {
				fontFamily: "'Archivo', sans-serif", fontWeight: "900", fontSize: "10px", letterSpacing: ".05em",
				textTransform: "uppercase", borderRadius: "999px", padding: "9px 11px", cursor: "pointer",
				color: needs ? MAG.ink : "#0b0b0f", background: needs ? "rgba(255,255,255,.1)" : MAG.lime, border: `1px solid ${MAG.line}`,
			},
		}, text);

	async function applyPatch(field, value, verb) {
		const ids = [...selected];
		const changes = [];
		for (const id of ids) {
			const before = await getMediaRecord(mediaType, id);
			if (!before) continue;
			await updateMediaItem(mediaType, id, { [field]: value });
			changes.push({ mediaType, id, before: { [field]: before[field] ?? null } });
		}
		finish();
		offerUndoChanges(`${verb} ${changes.length} item${changes.length === 1 ? "" : "s"}`, changes, { onDone: () => rerenderRoute() });
	}

	const ownershipOptions = mediaType === "book" || mediaType === "comic" ? BOOK_OWNERSHIP_STATUSES : OWNERSHIP_STATUSES;

	function buildBar() {
		return el(
			"div",
			{
				role: "toolbar",
				"aria-label": "Bulk actions",
				style: {
					position: "fixed", left: "8px", right: "8px", bottom: "calc(10px + env(safe-area-inset-bottom, 0px))", zIndex: "40",
					maxWidth: "704px", margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px",
					padding: "10px 12px", borderRadius: "16px", background: "rgba(14,12,20,.92)", border: `1px solid ${MAG.line}`,
					backdropFilter: "blur(14px)", webkitBackdropFilter: "blur(14px)", boxShadow: "0 10px 30px rgba(0,0,0,.5)",
				},
			},
			label,
			barButton("Status", () =>
				showActionSheet("Set status", ENGAGEMENT_STATUSES.map((s) => ({ label: statusLabel(s, mediaType), run: () => applyPatch("engagement_status", s, "Updated") })))),
			barButton("Own", () =>
				showActionSheet("Set ownership", ownershipOptions.map((s) => ({ label: statusLabel(s, mediaType), run: () => applyPatch("ownership_status", s, "Updated") })))),
			barButton("Group", async () => {
				const groups = (await listGroups()).filter((g) => g.media_type == null || g.media_type === mediaType);
				const addTo = async (groupId, name) => {
					const ids = [...selected];
					for (const id of ids) await addItemToGroup(groupId, id);
					finish();
					showToast(`Added ${ids.length} to “${name}”`);
					rerenderRoute();
				};
				showActionSheet("Add to a group", [
					...groups.map((g) => ({ label: g.name, run: () => addTo(g.id, g.name) })),
					{
						label: "+ New group…",
						variant: "primary",
						run: async () => {
							const name = (prompt("Name the new group") ?? "").trim();
							if (!name) return;
							const g = await createManualGroup({ name });
							await addTo(g.id, g.name);
						},
					},
				]);
			}),
			barButton("Delete", async () => {
				const ids = [...selected];
				if (!confirm(`Delete ${ids.length} item${ids.length === 1 ? "" : "s"} from your library?`)) return;
				const removed = [];
				for (const id of ids) {
					const rec = await deleteMediaItem(mediaType, id);
					if (rec) removed.push(rec);
				}
				finish();
				rerenderRoute();
				showToast(`Deleted ${removed.length} item${removed.length === 1 ? "" : "s"}`, {
					actionLabel: "Undo",
					durationMs: 7000,
					onAction: async () => {
						for (const rec of removed) await restoreMediaItem(mediaType, rec);
						rerenderRoute();
						showToast("Restored.");
					},
				});
			}),
			barButton("Done", () => stop(), { needs: false }),
		);
	}

	function start() {
		if (!container) return;
		active = true;
		button.textContent = "Done";
		container.dataset.selecting = "1";
		bar = buildBar();
		document.body.append(bar);
		paint();
	}

	function stop() {
		active = false;
		selected.clear();
		button.textContent = "Select";
		if (container) delete container.dataset.selecting;
		bar?.remove();
		bar = null;
		paint();
	}

	function finish() {
		stop();
	}

	return {
		button,
		attach(node) {
			container = node;
			node.addEventListener("click", onClick, true);
			// The list re-renders on sort / filter changes: re-mark the selection.
			new MutationObserver(() => active && paint()).observe(node, { childList: true, subtree: false });
			// Leaving the screen removes the floating bar.
			window.addEventListener("hashchange", () => stop(), { once: true });
		},
	};
}
